import { nameToHue } from '../../../shared/helpers/avatar';

/**
 * Static geometry for the floor-plan room tile, in SVG user units. The tile is
 * drawn in a fixed 200x150 viewBox (4:3) and scaled by CSS, so every value here
 * maps linearly to a percentage of the rendered tile (x/200, y/150) — the HTML
 * overlay relies on that for positioning. Pure module, no Angular imports.
 */

/** 4:3 drawing surface; keep in sync with the tile's CSS `aspect-ratio`. */
export const VIEW_BOX = '0 0 200 150';
export const VIEW_BOX_HEIGHT = 150;

/**
 * Which wall holds the door. `top` is deliberately not an option: the swing
 * arc would cut through the top seat dot and the name plate, and a top door
 * could only ever face the row above's corridor, which bottom doors on that
 * row already serve.
 */
export type DoorSide = 'bottom' | 'left' | 'right';

export interface DoorGeometry {
  /**
   * Room walls as one open path: a rounded rect (inset 8, corner radius 16)
   * traversed counterclockwise whose two endpoints are the doorposts of a
   * 30-unit door gap centered on the door side. An open path beats
   * `stroke-dasharray` for the gap — dash offsets need perimeter math
   * including the corner arcs and silently drift when any dimension changes.
   */
  wallPath: string;
  /** Open door leaf: hinged on one post, swung 90° into the room. */
  leafOpenPath: string;
  /** Classic architectural swing arc: quarter circle about the hinge. */
  swingPath: string;
  /** Closed leaf shuts the gap flush with the wall. */
  leafClosedPath: string;
  /**
   * Handle knob on the room side of the closed leaf, by the post opposite the
   * hinge — without the swing arc the shut leaf alone reads as plain wall.
   */
  handle: { cx: number; cy: number; r: number };
}

export const DOOR_GEOMETRIES: Record<DoorSide, DoorGeometry> = {
  bottom: {
    wallPath:
      'M 115 142 H 176 A 16 16 0 0 0 192 126 V 24 A 16 16 0 0 0 176 8 ' +
      'H 24 A 16 16 0 0 0 8 24 V 126 A 16 16 0 0 0 24 142 H 85',
    leafOpenPath: 'M 85 142 L 85 112',
    swingPath: 'M 115 142 A 30 30 0 0 0 85 112',
    leafClosedPath: 'M 85 142 L 115 142',
    handle: { cx: 109, cy: 138, r: 2 },
  },
  left: {
    wallPath:
      'M 8 90 V 126 A 16 16 0 0 0 24 142 H 176 A 16 16 0 0 0 192 126 ' +
      'V 24 A 16 16 0 0 0 176 8 H 24 A 16 16 0 0 0 8 24 V 60',
    leafOpenPath: 'M 8 60 L 38 60',
    swingPath: 'M 8 90 A 30 30 0 0 0 38 60',
    leafClosedPath: 'M 8 60 L 8 90',
    handle: { cx: 12, cy: 84, r: 2 },
  },
  right: {
    wallPath:
      'M 192 60 V 24 A 16 16 0 0 0 176 8 H 24 A 16 16 0 0 0 8 24 ' +
      'V 126 A 16 16 0 0 0 24 142 H 176 A 16 16 0 0 0 192 126 V 90',
    leafOpenPath: 'M 192 90 L 162 90',
    swingPath: 'M 192 60 A 30 30 0 0 0 162 90',
    leafClosedPath: 'M 192 60 L 192 90',
    handle: { cx: 188, cy: 66, r: 2 },
  },
};

/**
 * Corridors exist only between rooms, so a door may only face a side with
 * another room across the grid gap — never the outer edge of the grid. With a
 * single row there is no room below, and doors may then face neighboring
 * rooms or the bottom. A tile with no legal side at all (e.g. the lone room
 * in the last row's left column) falls back to bottom, read as a dead-end
 * corridor stub.
 */
export function allowedDoorSides(index: number, total: number, columns: number): DoorSide[] {
  const col = index % columns;
  const sides: DoorSide[] = [];
  if (total > columns && index + columns < total) sides.push('bottom');
  if (col > 0) sides.push('left');
  if (col < columns - 1 && index + 1 < total) sides.push('right');
  if (total <= columns) sides.push('bottom');
  return sides.length ? sides : ['bottom'];
}

/**
 * Deterministic door side per room, picked from the sides its grid position
 * allows (see `allowedDoorSides`). Salted so it does not correlate with the
 * wall color (which hashes the bare roomId), and stable across re-renders and
 * presence deltas — same reasoning as the avatar colors.
 */
export function doorSideFor(roomId: string, allowed: readonly DoorSide[]): DoorSide {
  if (!allowed.length) return 'bottom';
  return allowed[nameToHue(roomId + '/door') % allowed.length];
}

/** Conference table: rounded-corner rect centered at (100, 70). */
export const TABLE = { x: 68, y: 57, width: 64, height: 26, radius: 6 } as const;

/**
 * With a side (left/right) door, the bottom band holds no door — the table
 * group (table + seats + overflow badge) drops by this much to sit closer to
 * the room's true center. Bottom-door rooms keep the table higher so it
 * clears the swing arc.
 */
export const SIDE_DOOR_TABLE_SHIFT = 6;

export const SEAT_RADIUS = 5;

export interface SeatPosition {
  x: number;
  y: number;
}

// 8 fixed seats around the table: 3 above, 3 below, 1 left, 1 right, each 11
// units off the table edge. Listed in a balanced fill order (centers first,
// then the head seats, then corners) so partially occupied tables look evenly
// seated. Every seat clears every door's swing arc — with and without the
// side-door shift (closest case: bottom-left seat (76, 94) is ≈48.8 from the
// bottom hinge > 30 + seat radius) — and the walls, and the top row stays
// below the name plate band.
const SEATS: readonly SeatPosition[] = [
  { x: 100, y: 46 }, // top middle
  { x: 100, y: 94 }, // bottom middle
  { x: 57, y: 70 }, // left head
  { x: 143, y: 70 }, // right head
  { x: 76, y: 46 }, // top left
  { x: 124, y: 94 }, // bottom right
  { x: 124, y: 46 }, // top right
  { x: 76, y: 94 }, // bottom left
];

/**
 * The first `count` seat positions (capped at 8). Seats fill in a fixed slot
 * order, so existing chairs never move when the member count changes.
 */
export function seatPositions(count: number): readonly SeatPosition[] {
  return SEATS.slice(0, Math.max(0, Math.min(count, SEATS.length)));
}
